import React from "react";
import styled from "styled-components";
import { ArrowUp, ArrowDown, Dash } from "react-bootstrap-icons";
import { WidgetTheme } from "../../types/analytics";

const IndicatorContainer = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const ValueText = styled.span`
    font-weight: 500;
`;

const ChangeText = styled.span<{ $isPositive: boolean; $isNegative: boolean; $positiveColor: string; $negativeColor: string }>`
    font-size: 0.85em;
    color: ${props => props.$isPositive ? props.$positiveColor : props.$isNegative ? props.$negativeColor : "#6c757d"};
    display: inline-flex;
    align-items: center;
    gap: 2px;
`;

type POPIndicatorProps = {
    currentValue: number | null | undefined;
    previousValue: number | null | undefined;
    formatValue?: (value: number | null | undefined) => string;
    showPercentageChange?: boolean;
    invertColors?: boolean; // For metrics where decrease is good (e.g., bounce rate)
    theme?: WidgetTheme;
};

/**
 * Period-over-Period Indicator component
 * Displays a value with an arrow and colored percentage change
 */
const POPIndicator: React.FC<POPIndicatorProps> = ({
    currentValue,
    previousValue,
    formatValue = (v) => v?.toLocaleString() ?? "-",
    showPercentageChange = true,
    invertColors = false,
    theme
}) => {
    const positiveColor = theme?.positiveColor ?? "#b6ff00";
    const negativeColor = theme?.negativeColor ?? "#ff3a10";
    const formattedValue = formatValue(currentValue);
    
    // Calculate percentage change
    let percentageChange: number | null = null;
    let isPositive = false;
    let isNegative = false;
    
    if (currentValue != null && previousValue != null && previousValue !== 0) {
        percentageChange = ((currentValue - previousValue) / previousValue) * 100;
        
        if (invertColors) {
            isPositive = percentageChange < 0;
            isNegative = percentageChange > 0;
        } else {
            isPositive = percentageChange > 0;
            isNegative = percentageChange < 0;
        }
    }

    const renderChangeIndicator = () => {
        if (!showPercentageChange || percentageChange === null) {
            return null;
        }

        const absChange = Math.abs(percentageChange);
        const changeText = absChange < 0.1 ? "<0.1%" : `${absChange.toFixed(1)}%`;

        if (percentageChange > 0) {
            return (
                <ChangeText $isPositive={isPositive} $isNegative={isNegative} $positiveColor={positiveColor} $negativeColor={negativeColor}>
                    <ArrowUp size={12} />
                    {changeText}
                </ChangeText>
            );
        } else if (percentageChange < 0) {
            return (
                <ChangeText $isPositive={isPositive} $isNegative={isNegative} $positiveColor={positiveColor} $negativeColor={negativeColor}>
                    <ArrowDown size={12} />
                    {changeText}
                </ChangeText>
            );
        } else {
            return (
                <ChangeText $isPositive={false} $isNegative={false} $positiveColor={positiveColor} $negativeColor={negativeColor}>
                    <Dash size={12} />
                    0%
                </ChangeText>
            );
        }
    };

    return (
        <IndicatorContainer>
            <ValueText>{formattedValue}</ValueText>
            {renderChangeIndicator()}
        </IndicatorContainer>
    );
};

export default POPIndicator;
